import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Link from "@mui/material/Link";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link as RouterLink } from "react-router-dom";

import Box from "themeComponents/Box";
import Typography from "themeComponents/Typography";
import DashboardLayout from "themeComponents/LayoutContainers/DashboardLayout";
import DashboardNavbar from "themeComponents/Navbars/DashboardNavbar";
import Footer from "themeComponents/Footer";
import DetailedStatisticsCard from "themeComponents/Cards/StatisticsCards/DetailedStatisticsCard";
import Table from "themeComponents/Tables/Table";

import { courseApi } from "configs/api";

import "assets/css/nucleo-icons.css";
import "assets/css/nucleo-svg.css";

export type DashboardStats = {
  totalCourses: number;
  totalEnrollments: number;
  totalSavedCourses: number;
  totalAssignedToMe: number;
};

export type DashboardRecentCourse = {
  id: string;
  title: string;
  status: string;
  updatedAt: string;
  source: string;
};

function formatRecentWhen(iso: string): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString();
}

function Default(): React.JSX.Element {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [recentCourses, setRecentCourses] = useState<DashboardRecentCourse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [recentError, setRecentError] = useState<string | null>(null);

  const loadDashboard = useCallback(async () => {
    setLoading(true);
    setError(null);
    setRecentError(null);
    try {
      const [statsResult, recentResult] = await Promise.allSettled([
        courseApi.get("/api/courses/dashboard/stats"),
        courseApi.get("/api/courses/dashboard/recent-courses", { params: { limit: 5 } }),
      ]);

      if (statsResult.status === "fulfilled") {
        const payload = statsResult.value.data?.data ?? statsResult.value.data;
        setStats({
          totalCourses: Number(payload?.totalCourses ?? 0),
          totalEnrollments: Number(payload?.totalEnrollments ?? 0),
          totalSavedCourses: Number(payload?.totalSavedCourses ?? 0),
          totalAssignedToMe: Number(payload?.totalAssignedToMe ?? 0),
        });
      } else {
        setStats(null);
        setError("Could not load dashboard statistics.");
      }

      if (recentResult.status === "fulfilled") {
        const raw = recentResult.value.data?.data ?? recentResult.value.data;
        const list = Array.isArray(raw) ? raw : [];
        setRecentCourses(list as DashboardRecentCourse[]);
      } else {
        setRecentCourses([]);
        setRecentError("Could not load recent courses.");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadDashboard();
  }, [loadDashboard]);

  const display = (value: number): string | number => (loading ? "—" : value);

  const recentColumns = useMemo(
    () => [
      { name: "Course", align: "left" as const, width: "34%" },
      { name: "Source", align: "left" as const, width: "22%" },
      { name: "Last updated", align: "left" as const, width: "24%" },
      { name: "Status", align: "left" as const },
    ],
    [],
  );

  const recentRows = useMemo(() => {
    if (loading) {
      return [
        {
          Course: "Loading…",
          Source: "—",
          "Last updated": "—",
          Status: "—",
        },
      ];
    }
    if (recentCourses.length === 0) {
      return [
        {
          Course: "No courses in your catalog yet.",
          Source: "—",
          "Last updated": "—",
          Status: "—",
        },
      ];
    }
    return recentCourses.map((row) => ({
      Course: (
        <Link component={RouterLink} to={`/courses/${row.id}`} underline="hover">
          {row.title || "Untitled"}
        </Link>
      ),
      Source: row.source || "—",
      "Last updated": formatRecentWhen(row.updatedAt),
      Status: row.status || "—",
    }));
  }, [loading, recentCourses]);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Box py={3} sx={{ width: "100%", maxWidth: "100%", minWidth: 0 }}>
        {error ? (
          <Box mb={3}>
            <Typography variant="body2" color="error">
              {error}
            </Typography>
          </Box>
        ) : null}
        <Grid container spacing={3} mb={3} alignItems="stretch" sx={{ width: "100%" }}>
          <Grid item xs={12} sm={6} lg={3} sx={{ display: "flex", minWidth: 0, width: "100%" }}>
            <Box width="100%" minWidth={0}>
            <DetailedStatisticsCard
              uniformHeight
              title="Courses"
              count={display(stats?.totalCourses ?? 0)}
              icon={{
                color: "info",
                component: <Box component="i" className="ni ni-book-bookmark" sx={{ fontSize: "1.125rem" }} />,
              }}
              percentage={{ color: "success", count: "", text: "Visible to your role" }}
            />
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} lg={3} sx={{ display: "flex", minWidth: 0, width: "100%" }}>
            <Box width="100%" minWidth={0}>
            <DetailedStatisticsCard
              uniformHeight
              title="Enrollments"
              count={display(stats?.totalEnrollments ?? 0)}
              icon={{
                color: "success",
                component: <Box component="i" className="ni ni-badge" sx={{ fontSize: "1.125rem" }} />,
              }}
              percentage={{ color: "success", count: "", text: "Active / completed / enrolled" }}
            />
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} lg={3} sx={{ display: "flex", minWidth: 0, width: "100%" }}>
            <Box width="100%" minWidth={0}>
            <DetailedStatisticsCard
              uniformHeight
              title="Saved courses"
              count={display(stats?.totalSavedCourses ?? 0)}
              icon={{
                color: "warning",
                component: <Box component="i" className="ni ni-book-bookmark" sx={{ fontSize: "1.125rem" }} />,
              }}
              percentage={{ color: "success", count: "", text: "Library saves in scope" }}
            />
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} lg={3} sx={{ display: "flex", minWidth: 0, width: "100%" }}>
            <Box width="100%" minWidth={0}>
            <DetailedStatisticsCard
              uniformHeight
              title="Assigned to me"
              count={display(stats?.totalAssignedToMe ?? 0)}
              icon={{
                color: "error",
                component: <Box component="i" className="ni ni-send" sx={{ fontSize: "1.125rem" }} />,
              }}
              percentage={{ color: "success", count: "", text: "Active seat rows for your user" }}
            />
            </Box>
          </Grid>
        </Grid>

        <Box mb={3} mt={6}>
          <Typography
            variant="overline"
            color="secondary"
            display="block"
            sx={{ letterSpacing: 0.8, mb: 2 }}
          >
            Recent courses
          </Typography>
          {recentError ? (
            <Typography variant="body2" color="error" mb={1}>
              {recentError}
            </Typography>
          ) : null}
          <Card sx={{ overflow: "hidden" }}>
            <Box
              sx={{
                overflowX: "auto",
                WebkitOverflowScrolling: "touch",
                "& .MuiTable-root": { minWidth: 480 },
                "& .MuiTableRow-root:not(:last-child)": {
                  "& td": {
                    borderBottom: ({ borders: { borderWidth, borderColor } }: any) =>
                      `${borderWidth[1]} solid ${borderColor}`,
                  },
                },
              }}
            >
              <Table columns={recentColumns} rows={recentRows} />
            </Box>
          </Card>
        </Box>
      </Box>
      <Footer />
    </DashboardLayout>
  );
}

export default Default;
